<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-booking-confirm-main">
    <div class="booking-options">
      <div class="invoice-filters-and-table-box-controls">
        <div class="invoice-filters-summary-container">
          <span>{{ filterText }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="mainFilterIsOpen"
        >
          <div class="button-filter-content-container">
            <BoxContainer
              class="date-filter-container"
              :title="`Booking Date`"
              :isCheckBox="true"
              :isChecked.sync="dateFilterChecked"
            >
              <DateTimePicker
                :placeholder="`Start`"
                :value.sync="refreshStart"
                :showTime="false"
                :isDisabled="!dateFilterChecked"
              />
              <DateTimePicker
                :placeholder="`End`"
                :value.sync="refreshEnd"
                :showTime="false"
                :isDisabled="!dateFilterChecked"
              />
            </BoxContainer>

            <BoxContainer
              class="booking-no-filter-container"
              title="Search"
              :isChecked.sync="searchFilterChecked"
              :isCheckBox="true"
              style="margin-bottom: 5px"
            >
              <Dropdown
                placeholder="Search By"
                :items="searchFilterItems"
                itemKey="Search Criteria"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedSearchBy"
                :errorMessage.sync="searchByError"
                @selected="searchBySelected"
                validator="text,40"
              />
              <Dropdown
                placeholder="Search Criteria"
                :items="searchCriteriaItems"
                itemKey="name"
                :isDisabled="!searchFilterChecked || criteriaDisabled"
                :isRequired="searchFilterChecked"
                :selectedObject.sync="selectedCriteria"
                validator="text,40"
              />
              <Input
                placeholder="Search"
                :inputValue.sync="searchByValue"
                :isDisabled="!searchFilterChecked"
                :isRequired="searchFilterChecked"
                :errorMessage.sync="searchByValueError"
              />
            </BoxContainer>
            <div class="refresh-button-container">
              <button @click="refreshFilterClicked" :disabled="!dateFilterChecked && !searchFilterChecked">
                Refresh
              </button>
            </div>
          </div>
        </ButtonFilter>
      </div>

      <div class="table-box-container">
        <TableBox
          :tableData="jobsList"
          title="Import Jobs"
          :selectedRow.sync="selectedJob"
          :loading="tableLoading"
          :selectedIndex="selectedIndex"
        />
      </div>
      <div class="details-block">
        <DateTimePicker
          style="margin-left: 2px; margin-top: 10px"
          placeholder="Date"
          title="Date"
          :value.sync="dateInput"
          :showTime="false"
          :errorMessage.sync="errorMessages['reportDate']"
        />
      </div>

      <div class="details-block">
        <Input style="margin-right: 5px" placeholder="To" :inputValue.sync="sharedDetails['To']" validator="text,40" />
      </div>
      <div class="details-block">
        <Input
          style="margin-right: 5px"
          placeholder="Attn"
          :inputValue.sync="sharedDetails['Attn1']"
          validator="text,40"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Tel #"
          :inputValue.sync="sharedDetails['Tel1']"
          validator="text,20"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Fax #"
          :inputValue.sync="sharedDetails['Fax1']"
          validator="text,20"
        />
      </div>

      <div class="details-block">
        <Input style="margin-right: 5px" placeholder="CC" :inputValue.sync="sharedDetails['CC']" validator="text,40" />
      </div>
      <div class="details-block">
        <Input
          style="margin-right: 5px"
          placeholder="Attn"
          :inputValue.sync="sharedDetails['Attn2']"
          validator="text,40"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Tel #"
          :inputValue.sync="sharedDetails['Tel2']"
          validator="text,20"
        />
        <Input
          style="margin-right: 5px"
          placeholder="Fax #"
          :inputValue.sync="sharedDetails['Fax2']"
          validator="text,20"
        />
      </div>
      <div class="details-block">
        <RemarksBox title="Remarks" :text.sync="sharedDetails['RemarksInput']" :maxLength="850" />
      </div>
      <div class="details-block" style="justify-content: space-between; gap: 10px">
        <Input placeholder="From" :inputValue.sync="sharedDetails['Regards']" validator="text,40" />
        <Dropdown
          placeholder="Consignee"
          :items="consigneeDropdownItems"
          itemKey="value"
          :selectedObject.sync="selectedConsigneeDropdown"
        />
      </div>
      <div class="header-formatting-controls" style="justify-content: space-between; gap: 10px; display: flex; flex-direction: row;">
        <div class="header-formatting-column">
          <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showCompanyLetterHeadIsChecked" />
        </div>
        <div class="booking-signature-form">
          Signature
          <button @click="editSignature = !editSignature">{{ editSignature ? 'Back' : 'Edit' }}</button>
          <div class="booking-details-remarks">
            <div v-if="!editSignature" style="width: 100%">
              <div v-if="!selectedSignature">No signature selected</div>
              <img v-else :src="selectedSignature" class="selected-signature" />
            </div>
            <div v-else class="signature-another">
              <div v-if="allSignature.length == 0">No available signature</div>
              <div v-else class="choose-signature">
                Choose another signature
                <div v-if="selectedSignature" class="default-signature" style="margin-top: 5px; margin-bottom: 40px">
                  <button @click="defaultSignatureSelect()" style="background-color: red; color: white"
                    >Clear Selected Signature</button
                  >
                </div>
                <div class="default-signature" v-for="(s, i) in allSignature" :key="i">
                  Signature {{ i + 1 }}<button @click="defaultSignatureSelect(i)">Select</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="call-to-action-buttons" style="padding-top: 4px">
        <button @click="previewClicked">Preview Report</button>
      </div>
    </div>

    <div class="report-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :canClose="false"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span>Select Booking and click on <strong>Preview Report</strong> to get started</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {},
  data: function () {
    return {
      // Filter UI
      filterText: '',
      mainFilterIsOpen: false,
      dateFilterChecked: true,
      searchFilterChecked: false,
      refreshStart: new Date(new Date().setDate(new Date().getDate() - 3)),
      refreshEnd: new Date(),
      searchFilterItems: null,
      selectedSearchBy: null,
      searchByError: '',
      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedCriteria: null,
      criteriaDisabled: false,
      searchByValue: '',
      searchByValueError: '',

      // Table
      jobsList: [],
      selectedJob: [],
      selectedIndex: [],
      tableLoading: true,

      //Date
      errorMessages: {
        reportDate: '',
      },
      dateInput: null,

      //companyLetterHead
      showCompanyLetterHeadIsChecked: true,

      consigneeDropdownItems: [{ value: 'Actual Consignee' }, { value: 'FF as Consignee' }],
      selectedConsigneeDropdown: null,

      customerAndAgentDetails: null,

      //Signature
      editSignature: false,
      allSignature: [],
      selectedSignature: null,

      //Report
      sharedDetails: {
        Date: null,
        To: '',
        Attn1: '',
        Tel1: '',
        Fax1: '',
        CC: '',
        Signature: '',
        showSignature: false,
        Attn2: '',
        Tel2: '',
        Fax2: '',
        RemarksInput: '',
        Regards: '',
        showHeader: false,
        _header: '',
        frmCompany: '',
      },
      designerV3ReportData: [],
      designerV3SharedData: {},
      designerV3ReportFiles: [],
      showDesignerV3: false,

      //Report Data
      jobDetails: null,
    };
  },
  props: {},
  methods: {
    defaultSignatureSelect: function (index) {
      if (index >= 0) {
        this.selectedSignature = this.allSignature[index];
      } else {
        this.selectedSignature = null;
      }

      this.editSignature = false;
    },
    getSignatureDetails: async function () {
      this.allSignature = [];
      const userOptionsData = await this.$apiCallV2('/UserOptions/GetSettingsSystemUser?settings=UserOptions');
      if (userOptionsData.length > 0) {
        if (userOptionsData[0]['SettingsValue']) {
          const userOptionsObj = JSON.parse(userOptionsData[0]['SettingsValue']);
          if (userOptionsObj['Signature1']) {
            this.allSignature.push(userOptionsObj['Signature1']);
          }
          if (userOptionsObj['Signature2']) {
            this.allSignature.push(userOptionsObj['Signature2']);
          }
          if (userOptionsObj['Signature3']) {
            this.allSignature.push(userOptionsObj['Signature3']);
          }
        }
      }
    },
    searchBySelected: function (e) {
      if (e) {
        if (e['Search Criteria'] == 'Required Date') {
          this.selectedCriteria = this.searchCriteriaItems[0];
          this.criteriaDisabled = true;
        } else {
          this.criteriaDisabled = false;
        }
      }
    },
    async refreshFilterClicked() {
      this.jobsList = [];
      this.selectedJob = [];

      if (this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}, from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (this.dateFilterChecked && !this.searchFilterChecked) {
        this.filterText = `Filter by date from ${new Date(this.refreshStart).formatString('yyyy-MM-dd')} to ${new Date(this.refreshEnd).formatString('yyyy-MM-dd')}`;
      } else if (!this.dateFilterChecked && this.searchFilterChecked) {
        this.filterText = `Filter by ${this.selectedSearchBy['Search Criteria']} ${this.selectedCriteria['name']} ${this.searchByValue}`;
      }

      const dataToPost = {
        stjobdate: this.dateFilterChecked ? new Date(this.refreshStart).formatString('yyyy-MM-dd') : '',
        edjobdate: this.dateFilterChecked ? new Date(this.refreshEnd).formatString('yyyy-MM-dd') : '',
        jobtype: 'I',
        searchfield: this.searchFilterChecked ? this.selectedSearchBy['Search Criteria'] : '',
        searchcriteria: this.searchFilterChecked ? this.selectedCriteria['name'] : '',
        searchvalue: this.searchFilterChecked ? this.searchByValue : '',
      };

      this.tableLoading = true;
      this.$fullscreenLoading('Getting Booking Details');
      this.jobsList = await this.$apiCallV2(`/FreightForwarding/GetListJobTracking`, dataToPost);
      this.tableLoading = false;
      this.$fullscreenLoading();
      this.mainFilterIsOpen = false;
    },
    async refreshListing(selected) {
      let dataToPost = {};
      this.selectedCriteria = this.searchCriteriaItems[0];
      this.selectedSearchBy = this.searchFilterItems[0];
      dataToPost = {
        jobtype: 'I',
        searchfield: 'Job #',
        searchcriteria: 'EQUALS',
        searchvalue: selected,
      };
      this.tableLoading = true;
      this.$fullscreenLoading('Getting Booking Details');

      //reset to empty
      this.jobsList = [];

      //call api
      this.jobsList = await this.$apiCallV2(`/FreightForwarding/GetListJobTracking`, dataToPost);
      this.tableLoading = false;

      if (this.jobsList.length > 0) {
        // Find the index of the booking that matches `job no`
        const index = this.jobsList.findIndex((item) => item['Job No'] === selected);

        if (index !== -1) {
          this.$nextTick(() => {
            this.selectedJob = [this.jobsList[index]]; // Select the record
            this.selectedIndex = [index]; // Using this prop to highlight the row in TableBox, because the above code doesn't work
          });
        }

        this.customerAndAgentDetails = await this.$apiCallV2(`/PrintNoticeOfArrival/JobCustomerAndAgents`, {
          jobno: selected,
        });
        this.sharedDetails['To'] = this.customerAndAgentDetails[0]['Importer Name'] ?? '';
        this.sharedDetails['Attn1'] = 'EXPORT B/L DEPT.';
        this.sharedDetails['Fax1'] = this.customerAndAgentDetails[0]['SA Fax'] ?? '';
        this.sharedDetails['Tel1'] = this.customerAndAgentDetails[0]['SA Tel'] ?? '';
        this.sharedDetails['RemarksInput'] = this.customerAndAgentDetails[0]['Remarks'] ?? '';
      }

      this.$fullscreenLoading();
    },
    async previewClicked() {
      this.jobDetails =
        this.selectedConsigneeDropdown.value === 'FF as Consignee' ?
          await this.$apiCallV2(`/Report/GetJobDetails?jobno=${this.selectedJob[0]['Job No']}&addressto=FF`)
        : await this.$apiCallV2(`/Report/GetJobDetails?jobno=${this.selectedJob[0]['Job No']}`);
      const jobData = this.jobDetails['JobDetails']['Job'];

      //set company letter head
      if (this.showCompanyLetterHeadIsChecked == true) {
        this.sharedDetails.showHeader = true;
        this.sharedDetails._header = `https://dsmapi.logistics.systems/logo/CoHeaderOpr_${this.loginDetails.CoRefname}.png`;
      } else {
        this.sharedDetails.showHeader = false;
        this.sharedDetails._header = '';
      }

      this.sharedDetails.frmCompany = this.loginDetails.CoName;
      this.sharedDetails.Signature = this.selectedSignature;
      this.sharedDetails.showSignature = Boolean(this.selectedSignature);
      this.sharedDetails.Date = this.dateInput.formatString('yyyy-MM-dd');
      const grossWeight = jobData['Gross Weight'] ? `${jobData['Gross Weight']} KGS` : '0';
      const volume = jobData['Volume'] ? `${jobData['Volume']} M3` : '0';
      // jobData['containerNo'] = jobData[]
      jobData['goodDesc'] = jobData['GoodsDesc']?.map((value) => value['GoodsDescription']).join('') ?? '';
      jobData['shipMarks'] = jobData['ShipMarks']?.map((value) => value['ShipMark']).join('') ?? '';
      jobData['containers'] =
        jobData['ContDetail']?.map((value) => `${value['Container No']} / ${value['Seal No']}`).join(', ') ?? '';
      jobData['NoBLRequired'] =
        jobData['Original BLs'] || jobData['Original BLs'] === '0' ?
          'TELEX RELEASE'
        : `${jobData['Original BLs']} ORIGINAL`;
      jobData['MBL'] = jobData['Documents']?.find((doc) => doc['Doc Code'] === 'BL')?.['Doc Ref No'] || '';
      jobData['HBL'] = jobData['Documents']?.find((doc) => doc['Doc Code'] === 'HBL')?.['Doc Ref No'] || '';
      jobData['consigneeAddress'] = this.addressHelper('Consignee', jobData);
      jobData['nPartyAddress'] = this.addressHelper('NParty', jobData);
      jobData['shipperAddress'] =
        this.selectedConsigneeDropdown.value === 'FF as Consignee' ?
          this.addressHelperAgent(jobData)
        : this.addressHelper('Shipper', jobData);
      if (jobData['Documents']?.['Doc Code'] === 'K4') {
        jobData['showK4'] = true;
        jobData['K4'] = jobData['Documents']['Doc Ref No'];
      } else {
        jobData['showK4'] = false;
        jobData['K4'] = '';
      }

      jobData['grossWeightVolume'] = `${grossWeight} / ${volume}`;
      this.designerV3ReportData = [jobData];
      this.designerV3SharedData = this.sharedDetails;
      this.designerV3ReportFiles = ['Print_NoticeOfArrival.json'];
      this.showDesignerV3 = true;
    },
    addressHelper(key, jobData) {
      const add1 = jobData[`${key} Add1`] ? jobData[`${key} Add1`] + '\n' : '';
      const add2 = jobData[`${key} Add2`] ? jobData[`${key} Add2`] + '\n' : '';
      const add3 = jobData[`${key} Add3`] ? jobData[`${key} Add3`] + '\n' : '';
      const add4 = jobData[`${key} Add4`] ? jobData[`${key} Add4`] + '\n' : '';

      const postCodeCity = `${jobData[`${key} PostCode`] ? jobData[`${key} PostCode`] + ' ' : ''}${jobData[`${key} City`] ? jobData[`${key} City`] + '\n' : ''}`;
      const stateCountry = `${jobData[`${key} State`] ? jobData[`${key} State`] + ' ' : ''}${jobData[`${key} Country`] ? jobData[`${key} Country`] + '\n' : ''}`;
      return add1 + add2 + add3 + add4 + postCodeCity + stateCountry;
    },
    addressHelperAgent(jobData) {
      const add1 = jobData['ag_addr1'] ? jobData['ag_addr1'] + '\n' : '';
      const add2 = jobData['ag_addr2'] ? jobData['ag_addr2'] + '\n' : '';
      const add3 = jobData['ag_addr3'] ? jobData['ag_addr3'] + '\n' : '';
      const add4 = jobData['ag_addr4'] ? jobData['ag_addr4'] + '\n' : '';

      const postCodeCity = `${jobData['ag_postcode'] ? jobData['ag_postcode'] + ' ' : ''}${jobData['ag_city'] ? jobData['ag_city'] + '\n' : ''}`;
      const stateCountry = `${jobData['ag_state'] ? jobData['ag_state'] + ' ' : ''}${jobData['ag_country'] ? jobData['ag_country'] + '\n' : ''}`;

      return add1 + add2 + add3 + add4 + postCodeCity + stateCountry;
    },
  },
  async mounted() {
    this.$fullscreenLoading('Getting Login Details');
    this.loginDetails = await this.$apiCallV2('/Login/GetDetails');
    this.$fullscreenLoading();
    await this.getSignatureDetails();
    this.searchFilterItems = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'jobquery', module: 'FMS' });
    this.selectedConsigneeDropdown = this.consigneeDropdownItems[0];
    this.dateInput = new Date();
    if (this.$route.query['jobno']) {
      this.dateFilterChecked = false;
      this.searchFilterChecked = true;
      this.searchByValue = this.$route.query['jobno'];
      this.refreshListing(this.$route.query['jobno']);
    } else {
      this.refreshFilterClicked();
    }

    if (!this.selectedSearchBy) {
      this.selectedSearchBy = this.searchFilterItems[0];
    }

    if (!this.selectedCriteria) {
      this.selectedCriteria = this.searchCriteriaItems[2];
    }
  },
  watch: {
    async selectedJob(_, newValue) {
      const selected = newValue[0]?.['Job No'];
      if (selected) {
        const oldSelected = this.customerAndAgentDetails;
        const oldRemarks = oldSelected['Remarks']?.trim();
        this.customerAndAgentDetails = await this.$apiCallV2(`/PrintNoticeOfArrival/JobCustomerAndAgents`, {
          jobno: selected,
        });
        this.sharedDetails['To'] = this.customerAndAgentDetails[0]['Importer Name'] ?? '';
        this.sharedDetails['Attn1'] = 'EXPORT B/L DEPT.';
        this.sharedDetails['Fax1'] = this.customerAndAgentDetails[0]['SA Fax'] ?? '';
        this.sharedDetails['Tel1'] = this.customerAndAgentDetails[0]['SA Tel'] ?? '';
        this.sharedDetails['RemarksInput'] = this.sharedDetails['RemarksInput'] ?? ''; // If field becomes empty, it turns to null which messes up the trimming
        if (
          this.sharedDetails['RemarksInput'].trim() === '' ||
          this.sharedDetails['RemarksInput'].trim() === oldRemarks
        ) {
          this.sharedDetails['RemarksInput'] = this.customerAndAgentDetails[0]['Remarks'];
          this.selectedRemarks = null;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.print-booking-confirm-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;
  overflow: hidden;

  > .booking-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;
    width: 50%;
    overflow-y: auto;
    height: 100%;

    > .invoice-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .invoice-filters-summary-container {
        margin-right: 5px;
        align-content: center; // vertically align the summary text
        text-align: end; // horizontally align the summary text
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .invoice-no-filter-container {
          margin-top: 15px;
        }

        .customer-filter-container {
          margin-top: 15px;
        }

        .outstanding-and-job-type-filters {
          display: flex;
          flex-direction: row;

          > .outstanding-dropdown {
            margin-right: 5px;
          }

          > .job-type-dropdown {
            margin-left: 5px;
          }
        }

        .not-printed-filter {
          margin-top: 5px;
          display: flex;
          justify-content: flex-start;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .table-box-container {
      height: 40%;
      // height: 50%; // ============ change later
    }

    > .details-block {
      display: flex;
      width: 100%;
      position: relative;
      margin-bottom: 2px;

      > .details-column {
        width: calc(100% / 3);
        margin-right: 5px;
      }
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }

    .booking-signature-form {
      width: 50%;
      padding-top: 2px button {
        padding: 0 15px;
        float: right;
      }

      .booking-details-remarks {
        border: 1px solid rgb(200, 200, 200);
        border-radius: 3px;
        overflow: auto;
        display: flex;
        flex-wrap: wrap;
        padding-left: 5px;
        padding-right: 5px;
        min-height: 100px;

        .signature-another {
          width: 100%;

          .choose-signature {
            .default-signature {
              margin-top: 8px;

              button {
                float: right;
                padding: 2px 5px;
              }
            }
          }
        }
      }
    }
  }

  > .report-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }
}
</style>
